import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';
import pagesData from '../header-nav.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  /** When set, this page is a sub-nav item under a parent — not top-level. */
  parent?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface NavLink {
  label: string;
  href: string;
}

/**
 * Same aliases excluded from the main header bar. Keep in sync with Header.tsx
 * so the sitemap only lists primary navigation destinations.
 */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'terms-and-conditions',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
  'pet-policy',
]);

/**
 * Top-level header nav links only (dropdown children are omitted).
 */
function buildMainNavLinks(hasVirtualTours: boolean): NavLink[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (page.parent) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({ label: page.name, href: page.path }));
}

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const pages = useMemo(() => buildMainNavLinks(hasVirtualTours), [hasVirtualTours]);

  return (
    <section className="sitemap-content">
      <EditableCollectionBinding bindingPath="sitemapcontent.virtual_tours" label="Virtual Tours">
        <nav aria-label="Sitemap" className="sitemap-content__nav">
          <ul className="sitemap-content__list">
            {pages.map((page) => (
              <li key={page.href}>
                <a href={page.href} className="sitemap-content__link">
                  {page.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </EditableCollectionBinding>

      <style>{`
        .sitemap-content {
          position: relative;
          background-color: #f7f6f4;
          background-image:
            radial-gradient(rgba(52, 46, 31, 0.035) 0.6px, transparent 0.6px),
            radial-gradient(rgba(52, 46, 31, 0.025) 0.6px, transparent 0.6px);
          background-size: 3px 3px, 5px 5px;
          background-position: 0 0, 1.5px 1.5px;
          padding: 4.5rem 1.5rem 5rem;
        }
        @media (min-width: 768px) {
          .sitemap-content {
            padding: 5.5rem 2rem 6rem;
          }
        }

        .sitemap-content__nav {
          max-width: 40rem;
          margin: 0 auto;
        }

        .sitemap-content__list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.75rem;
        }
        @media (min-width: 768px) {
          .sitemap-content__list {
            gap: 2rem;
          }
        }

        .sitemap-content__link {
          display: block;
          text-align: center;
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 1.0625rem;
          font-weight: 400;
          line-height: 1.4;
          letter-spacing: 0.01em;
          color: #555555;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .sitemap-content__link:hover,
        .sitemap-content__link:focus-visible {
          color: var(--vibe-color-primary, #1a1a2e);
          outline: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SitemapContent;
